import { AccountDataService } from '../../services';
import { CheckPinResult } from '../../models';
import { Injectable, inject } from '@angular/core';
import { PinRequestResult } from '../../types';
import { UserFacade } from '../../authentication';
import { wait } from '../../helpers';

/**
 * Service that centralized access to user credentials.
 */
@Injectable({
  providedIn: 'root',
})
export class CredentialsService {
  private readonly accountDataService = inject(AccountDataService);
  private readonly user = inject(UserFacade);

  private lastValidation: number | null = null;
  private lastValidationResult: boolean | null = null;
  private validationInterval = 15 * 60 * 1000; // 15 minutes

  /**
   * checks whether pin is valid.
   * @param pinToCheck pin that have to be checked.
   * @returns boolean Promise with information whether pinToCheck is valid. If not valid there is a message why it is not valid
   */
  public async checkPin(pinToCheck: string): Promise<CheckPinResult> {
    if (!this.user.hasSecondFactor()) {
      return {
        success: true,
        message: '',
      };
    }

    return this.accountDataService.checkIfPinIsValid(pinToCheck);
  }

  public async getPin(): Promise<string | null> {
    if (!this.user.hasSecondFactor()) {
      return '';
    }

    return this.user.getSecondFactor();
  }

  public async getUserSecret(): Promise<string | undefined> {
    return this.user.userSecret();
  }

  public async hasPinSaved(): Promise<boolean> {
    return !!(await this.user.getSecondFactor());
  }

  public async requestPin(): Promise<PinRequestResult> {
    const requiresPin = this.user.hasSecondFactor();

    // on first load the user service isn't always ready, so we check again after half a second
    if (requiresPin === undefined) {
      await wait(500);
      return this.requestPin();
    }

    if (!requiresPin) {
      return {
        success: true,
        hasPin: false,
      };
    }

    if (await this.validateSecondFactor()) {
      return {
        success: true,
        hasPin: true,
        pin: (await this.user.getSecondFactor()) || '',
      };
    }

    return {
      success: false,
      hasPin: true,
    };
  }

  public resetPin(): Promise<void> {
    return this.user.resetSecondFactor();
  }

  public async setPin(pin: string): Promise<void> {
    return this.user.setSecondFactor(pin);
  }

  /**
   * Validates the second factor for the user.
   * This method checks if the user has a second factor configured and verifies it using the provided mechanism.
   *
   * @return {Promise<boolean>} A promise that resolves to a boolean indicating whether the second factor validation was successful.
   */
  public async validateSecondFactor(): Promise<boolean> {
    if (!(await this.user.getSecondFactor())) {
      return false;
    }

    const currentTimestamp = Date.now();
    if (
      this.lastValidation &&
      currentTimestamp - this.lastValidation < this.validationInterval &&
      this.lastValidationResult
    ) {
      return true;
    }

    const secondFactor = await this.user.getSecondFactor();
    if (!secondFactor) {
      return false;
    }
    const pinCheckResult = await this.checkPin(secondFactor);
    this.lastValidation = currentTimestamp;
    this.lastValidationResult = pinCheckResult.success;
    return !!pinCheckResult?.success;
  }
}
